import { useAppStore } from "@hooks/useAppStore";
import type { SessionConfigFiltersType } from "@memorang/constants/config/schema";
import {
	DEFAULT_DURATION_MINUTES,
	DURATION_OPTIONS,
} from "@memorang/ui/components/QuickStudyModal/durationOptions";
import type { Distribution } from "@memorang/ui/types";
import { useCallback, useMemo, useReducer } from "react";
import { itemCountForDuration } from "../helpers/duration";
import {
	calculateQuestionCount,
	createDistributionReducer,
	createInitialDistributionState,
	getOptimalValue,
} from "../helpers/filter";
import type { CheckboxState } from "../types/filter";

const sumDistribution = (dist: Distribution) =>
	Object.keys(dist).reduce(
		(acc, key) => acc + dist[key as keyof Distribution],
		0,
	);

export const useDistributionState = ({
	availableDistribution,
	distributionFilterItems,
	showDistributionFilters,
	studyRecommendedTopics,
}: {
	availableDistribution: Distribution;
	distributionFilterItems: SessionConfigFiltersType;
	showDistributionFilters?: boolean;
	studyRecommendedTopics?: boolean;
}) => {
	const showItemsSlider = useAppStore(
		(state) => state.app.sessionConfig?.showItemsSlider ?? false,
	);
	const showDurationSelection = useAppStore(
		(state) => state.app.sessionConfig?.showDurationSelection ?? false,
	);

	const getDefaultRequiredItems = useCallback(
		(available: number) => {
			if (showDurationSelection) {
				return available > 0
					? itemCountForDuration(DEFAULT_DURATION_MINUTES, available)
					: 0;
			}
			if (!showItemsSlider) {
				return available;
			}
			return available > 50 ? 20 : getOptimalValue(available, 10);
		},
		[showDurationSelection, showItemsSlider],
	);

	const getCheckboxSeedTarget = useMemo(
		() =>
			showDurationSelection
				? (available: number) =>
						available > 0
							? itemCountForDuration(Math.max(...DURATION_OPTIONS), available)
							: 0
				: undefined,
		[showDurationSelection],
	);

	const numAvailableItems = sumDistribution(availableDistribution);
	const defaultRequiredItems = getDefaultRequiredItems(numAvailableItems);

	const distributionReducer = createDistributionReducer({
		capItemCount: showItemsSlider || showDurationSelection,
		showDistributionFilters,
		distributionFilterItems,
		studyRecommendedTopics,
		defaultRequiredItems,
		getDefaultRequiredItems,
		getCheckboxSeedTarget,
	});

	const [distributionState, dispatch] = useReducer(
		distributionReducer,
		undefined,
		() =>
			createInitialDistributionState(
				availableDistribution,
				numAvailableItems,
				defaultRequiredItems,
				showDurationSelection ? DEFAULT_DURATION_MINUTES : undefined,
			),
	);

	const {
		finalAvailableDist,
		requiredDistribution,
		numRequiredItems,
		availableItems,
		selectedDistributionCheckboxMap,
		selectedDurationMinutes,
	} = distributionState;

	const numTotalItemsFiltered = useMemo(
		() =>
			selectedDistributionCheckboxMap
				? calculateQuestionCount(
						selectedDistributionCheckboxMap,
						finalAvailableDist,
					)
				: 0,
		[finalAvailableDist, selectedDistributionCheckboxMap],
	);

	const handleDistributionCheckboxState = useCallback(
		(checkboxState: CheckboxState) => {
			dispatch({ type: "checkbox-selection-changed", checkboxState });
		},
		[],
	);

	const applyAvailableDistribution = useCallback((newDist: Distribution) => {
		dispatch({ type: "distribution-updated", distribution: newDist });
	}, []);

	const handleSliderChange = useCallback((value: number) => {
		dispatch({ type: "required-item-count-changed", count: value });
	}, []);

	const handleDurationChange = useCallback((minutes: number) => {
		dispatch({ type: "duration-changed", minutes });
	}, []);

	return {
		finalAvailableDist,
		requiredDistribution,
		numRequiredItems,
		availableItems,
		selectedDistributionCheckboxMap,
		numTotalItemsFiltered,
		handleDistributionCheckboxState,
		handleSliderChange,
		selectedDurationMinutes,
		handleDurationChange,
		applyAvailableDistribution,
	};
};
